import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { SectionWrapper, Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import {
  FormatAsCurrencyForLocale,
  DateToShortOrLongReadable,
  RichTextParser
} from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { PolicyDetailsPropTypes } from '../../../propTypes/petDetails';
import { FREQUENCY_LABELS } from '../../../constants/constants';
import { useResponsiveConfig } from '../../../hooks/responsiveConfigSwap';
import { useLazyGetOutstandingPolicyPaymentQuery } from '../../../data/premiumsandpayments';
import { getNextPaymentDateText } from '../../../helpers/policyHelpers';
import { legacyPolicyNumberHandler } from '../../../helpers/paymentHelper';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { TooltipTrigger } from '../../tooltipTrigger/tooltipTrigger';
import { BrandContext } from '../../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { setFullPolicyNumber } from '../../../store/slice/petDetailsSlice';
import RenewalBanner from '../../renewal/banner/renewalBanner';
import { useGetGeneralTooltipContentQuery } from '../../../data/kontentAi';
import { GENERAL_TOOLTIP_CODENAME } from '../../../constants/cmsCodeName';
import { SLICES } from '../../../constants/slices';

export const PolicyDetailsList = ({ policyDetails, FullPolicyNumber, PartnerNumber }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auth0AccessToken } = useSelector((state) => state?.[SLICES.AUTHENTICATION]?.authDetails || {});
  const [getOutstandingPayment] = useLazyGetOutstandingPolicyPaymentQuery();
  const [amountOverdue, setAmountOverdue] = useState(0);
  const displaySettings = useResponsiveConfig({
    configDefinition: { default: { isFixed: true }, 992: { isFixed: false } }
  });

  const {
    activeBrandContext: {
      cspConfiguration: { faqRedirection }
    }
  } = useContext(BrandContext);

  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isExcessEnabled = cspConfiguration?.IsExcessEnabled || false;
  const isPayNowEnabled = cspConfiguration?.IsPayNowEnabled;
  const isRenewalHubEnabled = cspConfiguration?.IsRenewalHubEnabled || false;
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const isV3PenTestRemediationEndpointsEnabled = cspConfiguration?.IsV3PenTestRemediationEndpointsEnabled;

  const { data: generalTooltip } = useGetGeneralTooltipContentQuery({
    previewEnabled: isKontentAiPreviewEnabled,
    element: [GENERAL_TOOLTIP_CODENAME.NEXT_COLLECTION_AMOUNT_TOOLTIP]
  });
  const nextCollectionAmountTooltip =
    generalTooltip?.content?.[GENERAL_TOOLTIP_CODENAME.NEXT_COLLECTION_AMOUNT_TOOLTIP]?.[
      GENERAL_TOOLTIP_CODENAME.NEXT_COLLECTION_AMOUNT_TOOLTIP
    ]?.value || '';

  const handleGoToPaynow = (policyNumber) => {
    dispatch(setFullPolicyNumber(policyNumber));
    navigate(CSPUrlsMap.payNow.url);
  };

  const {
    Excess,
    ExcessType,
    LevelOfCover,
    BenefitPercentage,
    PolicyStartDate,
    PolicyEndDate,
    PaymentFrequency,
    NextCollectionAmount,
    NextPaymentDate,
    Premium,
    Underwriter,
    PolicyCover
  } = policyDetails;

  useEffect(() => {
    if (
      isPayNowEnabled &&
      FullPolicyNumber &&
      PartnerNumber &&
      isV3PenTestRemediationEndpointsEnabled !== undefined &&
      (!isV3PenTestRemediationEndpointsEnabled || auth0AccessToken)
    ) {
      const fetchOutstandingPayment = async () => {
        try {
          const result = await getOutstandingPayment({
            policyNumber: legacyPolicyNumberHandler(FullPolicyNumber),
            partnerNumber: PartnerNumber,
            auth0Token: auth0AccessToken,
            isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
          }).unwrap();

          const totalAmount = result?.PremiumInstallment?.reduce((sum, item) => {
            return sum + item.DueAmount;
          }, 0);

          setAmountOverdue(totalAmount);
        } catch (error) {
          throw Error('Error fetching outstanding payment data:', error);
        }
      };
      fetchOutstandingPayment();
    }
  }, [
    FullPolicyNumber,
    PartnerNumber,
    getOutstandingPayment,
    setAmountOverdue,
    auth0AccessToken,
    isV3PenTestRemediationEndpointsEnabled,
    isPayNowEnabled
  ]);

  const startDate = new Date(PolicyStartDate);
  const endDate = new Date(PolicyEndDate);
  const overdueAmount = amountOverdue || 0;

  const frequencyKey = (PaymentFrequency || '').toUpperCase();
  const frequencyLabel = FREQUENCY_LABELS[frequencyKey] || FREQUENCY_LABELS[PaymentFrequency] || PaymentFrequency;
  const nextPaymentDateText = getNextPaymentDateText({
    nextPaymentDate: NextPaymentDate,
    frequencyKey,
    formatDate: DateToShortOrLongReadable
  });
  const shouldShowUnderwriter = process.env.REACT_APP_ENV !== 'prod';

  const policyNumberForBanner = policyDetails.policyNumber ?? policyDetails.PolicyNo ?? null;
  return (
    <SectionWrapper
      heading="Policy details"
      customCssClass="policyDetailsList"
      {...displaySettings}
    >
      {isRenewalHubEnabled && (
        <div className="renewalBannerWrapper">
          <RenewalBanner
            type="inline"
            policyNumber={policyNumberForBanner}
            location="my_pets"
          />
        </div>
      )}
      <ul>
        <li>
          <strong>Policy no.</strong>
          <em>
            {FullPolicyNumber}
            {shouldShowUnderwriter && Underwriter ? ` - ${Underwriter}` : ''}
          </em>
        </li>
        <li>
          <strong>Level of cover</strong>
          <em>{LevelOfCover}</em>
        </li>
        <li>
          <strong>Annual benefit limit</strong>
          <em>{FormatAsCurrencyForLocale(PolicyCover?.AnnualBenefitLimit ?? 0, { trimZeroDecimals: true })}</em>
        </li>
        <li>
          <strong>Benefit percentage</strong>
          <em>{BenefitPercentage}%</em>
        </li>
        {isExcessEnabled && (
          <li className="policyRow">
            <strong>{ExcessType}</strong>
            <em>{FormatAsCurrencyForLocale(Excess, { trimZeroDecimals: true })} </em>
          </li>
        )}
        <li>
          <strong>Policy period</strong>
          <em>
            {DateToShortOrLongReadable(startDate)} – {DateToShortOrLongReadable(endDate)}
          </em>
        </li>
        <li>
          <strong>Payment frequency</strong>
          <em>{frequencyLabel}</em>
        </li>
        <li className="policyRow">
          <strong>Next collection amount</strong>
          <em>
            {FormatAsCurrencyForLocale(NextCollectionAmount)}{' '}
            <TooltipTrigger
              id="next-collection-amount-tip"
              trigger={<Icon icon="information" />}
              content={<>{RichTextParser(nextCollectionAmountTooltip)}</>}
            />
          </em>
        </li>
        <li>
          <strong>Next payment date</strong>
          <em>{nextPaymentDateText}</em>
        </li>
        {isPayNowEnabled && (
          <li>
            <strong>Amount overdue</strong>
            <em>
              {FormatAsCurrencyForLocale(overdueAmount)}
              &nbsp;
              {overdueAmount > 0 && (
                <button
                  type="button"
                  onClick={() => handleGoToPaynow(FullPolicyNumber)}
                  className="button primary text_only payNowButton"
                >
                  Pay now
                </button>
              )}
            </em>
          </li>
        )}
      </ul>
    </SectionWrapper>
  );
};

PolicyDetailsList.propTypes = {
  policyDetails: PolicyDetailsPropTypes,
  FullPolicyNumber: PropTypes.string,
  PartnerNumber: PropTypes.string
};
